import React, { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import InventoryPicker from '../InventoryPicker'
import { ioGet, ioSend } from './api'
import { btnPrimary, btnSecondary, cardClass, inputClass } from './ui'

const emptyPkg = () => ({
  name: '',
  published: false,
  description: '',
  displayUnitIds: [],
  criteriaIds: [],
  priceCents: '',
  flightDefaults: {
    durationDays: 7,
    adDurationSec: 15,
    saturation: '1 per loop',
    daysOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    startTime: '00:00',
    endTime: '23:59',
  },
})

export default function PackagePage() {
  const { isAdmin } = useAuth()
  const [packages, setPackages] = useState([])
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyPkg())
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => ioGet('/api/io/packages').then((d) => setPackages(d.packages || [])).catch((e) => setError(e.message))
  useEffect(() => { load() }, [])

  const save = async () => {
    if (!form.name.trim()) {
      setError('Package name is required')
      return
    }
    setBusy(true)
    setError('')
    const body = {
      name: form.name.trim(),
      published: form.published,
      description: form.description,
      displayUnitIds: form.displayUnitIds,
      criteriaIds: form.criteriaIds,
      flightDefaults: form.flightDefaults,
      priceCents: form.priceCents === '' ? null : Math.round(Number(form.priceCents) * 100),
    }
    try {
      if (editing) await ioSend(`/api/io/packages/${editing}`, 'PUT', body)
      else await ioSend('/api/io/packages', 'POST', body)
      setEditing(null)
      setForm(emptyPkg())
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const startEdit = (p) => {
    setEditing(p.id)
    setForm({
      name: p.name,
      published: p.published,
      description: p.description || '',
      displayUnitIds: p.display_unit_ids || [],
      criteriaIds: p.criteria_ids || [],
      priceCents: p.price_cents != null ? (p.price_cents / 100).toString() : '',
      flightDefaults: p.flight_defaults || emptyPkg().flightDefaults,
    })
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Packages</h1>
        <p className="text-sm text-gray-500">Ready-to-sell bundles of screens. Buyers add a package as a campaign line item.</p>
      </div>

      <div className={`${cardClass} overflow-hidden`}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Screens</th>
              <th className="px-4 py-2">Price</th>
              <th className="px-4 py-2">Status</th>
              {isAdmin && <th className="px-4 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {packages.map((p) => (
              <tr key={p.id} className="border-t border-gray-200 dark:border-gray-700">
                <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{p.name}</td>
                <td className="px-4 py-2">{(p.display_unit_ids || []).length}</td>
                <td className="px-4 py-2">{p.price_cents != null ? `$${(p.price_cents / 100).toFixed(2)}` : '—'}</td>
                <td className="px-4 py-2">{p.published ? 'On' : 'Off'}</td>
                {isAdmin && (
                  <td className="px-4 py-2"><button type="button" className="text-blue-600" onClick={() => startEdit(p)}>Edit</button></td>
                )}
              </tr>
            ))}
            {!packages.length && <tr><td className="px-4 py-6 text-gray-500" colSpan={5}>No packages yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {isAdmin && (
        <div className={`${cardClass} p-6 space-y-4`}>
          <h2 className="font-medium text-gray-900 dark:text-white">{editing ? 'Edit package' : 'Create digital package'}</h2>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <input className={inputClass} placeholder="Package name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Published (available on the network)
          </label>
          <textarea className={inputClass} rows={2} placeholder="Value proposition" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input className={inputClass} type="number" placeholder="Flight days" value={form.flightDefaults.durationDays}
              onChange={(e) => setForm({ ...form, flightDefaults: { ...form.flightDefaults, durationDays: Number(e.target.value) } })} />
            <input className={inputClass} type="number" placeholder="Ad duration (sec)" value={form.flightDefaults.adDurationSec}
              onChange={(e) => setForm({ ...form, flightDefaults: { ...form.flightDefaults, adDurationSec: Number(e.target.value) } })} />
            <input className={inputClass} placeholder="Saturation" value={form.flightDefaults.saturation}
              onChange={(e) => setForm({ ...form, flightDefaults: { ...form.flightDefaults, saturation: e.target.value } })} />
            <input className={inputClass} type="number" step="0.01" placeholder="Price (USD)" value={form.priceCents}
              onChange={(e) => setForm({ ...form, priceCents: e.target.value })} />
          </div>
          <InventoryPicker
            selectedIds={form.displayUnitIds}
            onChange={(ids) => setForm({ ...form, displayUnitIds: ids })}
            title="Fixed screens"
            subtitle="Hand-pick faces in this package"
          />
          <div className="flex gap-2">
            <button type="button" className={btnPrimary} disabled={busy} onClick={save}>{editing ? 'Save' : 'Create'}</button>
            {editing && (
              <button type="button" className={btnSecondary} onClick={() => { setEditing(null); setForm(emptyPkg()) }}>Cancel</button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
